// Configuration (02-DESIGN/06-configuration/01-the-configuration-service.md
// § the console: Configuration; 01-the-console.md § the surfaces, § the
// words: *settings* and its *version*; § conventions: a table): one table of
// the workspace's settings documents — workload, settings, version — sorted
// and filtered, for an admin. It is built from the workload service's list
// paired with each workload's list of documents, whose entries carry a key
// and a version and no word on the workload. A workload with no documents has no
// row, and a retired workload's documents are not listed. A row opens its
// document's page, `configuration/<workload>/<document>` (DocumentPage.tsx),
// as text under the version guard, and the workload's name is a link to its
// page (§ conventions: a name is a link); *Add settings* is a form in the
// table's place, and the new row is marked when it closes; a delete from the
// row menu updates the table from the reply's envelope. A workload's own page
// keeps its documents (WorkloadSettings).

import { identity } from "@impire-io/sdk";
import { Plus } from "lucide-react";
import { useState } from "react";

import { tenantPath } from "../../address/address.ts";
import { CODE_NO_SERVICE, isRefusal } from "../../platform/bus.ts";
import { usePage } from "../../platform/session.tsx";
import { pageOffered } from "../../roles/offers.ts";
import { useFormState } from "../../ui/form.tsx";
import { NameLink } from "../../ui/ident.tsx";
import { Button, Empty, NotAllowed, Notice, PageHead, Pending, useLoad } from "../../ui/components.tsx";
import { Table, type Column, type MenuEntry } from "../../ui/table.tsx";
import { DOCUMENT_REQUIRED, DOCUMENT_SETTINGS, type Entry } from "../../wire/config.ts";
import { listSubject, RegistrationState, SERVICE, type ListReply } from "../../wire/workloads.ts";
import { useSurface } from "../surface.tsx";
import { useManaged } from "../workspaces/managed.ts";
import { applied, documentAddress, rowsOf, type Owner, type Row } from "./documents.ts";
import { AddDocument } from "./WorkloadSettings.tsx";

/** Read is what the page read: the live workloads and the table's rows. */
interface Read {
  owners: Owner[];
  rows: Row[];
}

export function ConfigurationPage() {
  const s = useSurface();
  const { platform } = usePage();
  const managed = useManaged();
  const canEdit = s.offered("configuration:any");
  const model = platform.images?.[identity.Image.inference] || undefined;

  const [read] = useLoad<Read>("configuration/documents", async () => {
    const list = await s.bus.call<ListReply>(SERVICE, listSubject(), undefined);
    const owners = list.workloads
      .filter((w) => w.state !== RegistrationState.retired)
      .map((w) => ({ id: w.id, name: w.name !== "" ? w.name : w.id, image: w.image }));
    const cfg = s.bus.configuration();
    const lists = new Map(await Promise.all(owners.map(async (w) => [w.id, await cfg.list(w.id)] as const)));
    return { owners, rows: rowsOf(owners, lists, DOCUMENT_SETTINGS) };
  });

  // The table as the page last knew it: the read, then each save and delete
  // since that read applied from the reply's envelope.
  const base = read.state === "ready" ? read.value : undefined;
  const [edits, setEdits] = useState<{ of: Read | undefined; entries: Entry[] }>({
    of: undefined,
    entries: [],
  });
  const owners = base?.owners ?? [];
  const rows =
    base === undefined
      ? []
      : (edits.of === base ? edits.entries : []).reduce(
          (r, e) => applied(r, base.owners, e, DOCUMENT_SETTINGS),
          base.rows,
        );

  const form = useFormState<"add">();
  const [mark, setMark] = useState<string | undefined>(undefined);
  const [note, setNote] = useState<string | undefined>(undefined);
  const [actErr, setActErr] = useState<unknown>(undefined);

  const apply = (e: Entry) => {
    setEdits((p) => ({ of: base, entries: p.of === base ? [...p.entries, e] : [e] }));
  };
  // In a managed workspace the required document is the steward's, written
  // from the blueprint: read here, never written (13-blueprints/03-required.md § the console).
  const locked = (r: Row) => r.document === DOCUMENT_REQUIRED && managed.managed;
  const isModel = (image: string) => model !== undefined && image === model;

  const remove = (r: Row) => {
    setActErr(undefined);
    s.bus
      .configuration()
      .delete(r.workload, r.document)
      .then(
        (e) => {
          apply(e);
          setNote(`${r.name}'s ${r.document} is gone.`);
        },
        (err: unknown) => {
          setActErr(err);
        },
      );
  };

  // The workload is a link on the row (§ conventions: a name is a link); the menu holds the delete alone.
  const menu = (r: Row): MenuEntry[] => {
    if (!canEdit || locked(r)) {
      return [];
    }
    return [
      {
        label: "Delete",
        danger: true,
        onSelect: () => {
          remove(r);
        },
        confirm: {
          title: `Delete ${r.name}'s ${r.document}?`,
          consequence: `Not reversible: ${r.document} is gone, and ${r.name} finds nothing there the next time it reads it.`,
        },
      },
    ];
  };

  const noService = read.state === "refused" && isRefusal(read.err, CODE_NO_SERVICE);
  const add =
    canEdit && read.state === "ready" && owners.length > 0 ? (
      <Button
        kind="primary"
        onClick={() => {
          form.show("add");
        }}
      >
        <Plus strokeWidth={1.8} aria-hidden="true" />
        Add settings
      </Button>
    ) : undefined;

  // *Add settings* replaces the table with its form (01-the-console.md § conventions: a form).
  if (form.open === "add" && add !== undefined) {
    return (
      <AddDocument
        workload=""
        owners={owners}
        hint={false}
        hintOf={(w) => isModel(owners.find((o) => o.id === w)?.image ?? "")}
        back="Configuration"
        onClose={form.close}
        onSaved={(e) => {
          form.close();
          apply(e);
          setMark(e.key);
          setNote(`Saved as version ${e.revision}.`);
        }}
      />
    );
  }

  const workloadTo = pageOffered(s.role, "workloads")
    ? (r: Row) => tenantPath(s.tenant, "workloads", r.workload)
    : undefined;

  return (
    <>
      <PageHead
        title="Configuration"
        line={`What each of ${s.name}'s workloads reads while it runs, in one table.`}
        action={add}
      />
      {actErr !== undefined && <NotAllowed err={actErr} />}
      {note !== undefined && (
        <Notice kind="ok">
          {note}{" "}
          <button
            type="button"
            className="text-link"
            onClick={() => {
              setNote(undefined);
            }}
          >
            Dismiss
          </button>
        </Notice>
      )}
      {read.state === "pending" && <Pending what="Reading the settings" />}
      {noService && (
        <Notice title="No configuration service">
          Nothing serves configuration on this install yet, so there are no settings to show.
        </Notice>
      )}
      {read.state === "refused" && !noService && <NotAllowed err={read.err} what="Configuration" />}
      {read.state === "ready" && owners.length === 0 && (
        <Empty>
          Nothing runs for {s.name} yet. Settings belong to a workload: add one under Workloads, or a model
          under Models.
        </Empty>
      )}
      {read.state === "ready" && owners.length > 0 && rows.length === 0 && (
        <Empty action={add}>
          No settings yet. What a workload reads while it runs goes here, as text, and it sees a change
          without a restart.
        </Empty>
      )}
      {rows.length > 0 && (
        <Table
          label="Settings"
          rows={rows}
          rowKey={(r) => r.key}
          sort={{ key: "workload", dir: "asc" }}
          columns={columns(isModel, managed.managed ? managed.by : undefined, workloadTo)}
          to={(r) => documentAddress(s.tenant, r.workload, r.document)}
          menu={menu}
          mark={mark}
        />
      )}
    </>
  );
}

/**
 * columns are the table's: the document, which is the row's own link to its
 * page; the workload by its display name, a link to its page where offered —
 * never inside the row's link cell (§ conventions: a name is a link); its version.
 */
function columns(
  isModel: (image: string) => boolean,
  steward: string | undefined,
  workloadTo: ((r: Row) => string) | undefined,
): Column<Row>[] {
  return [
    {
      key: "document",
      header: "Settings",
      width: "minmax(0, 1.4fr)",
      value: (r) => r.document,
      cell: (r) => (
        <span className="cell">
          <span className="cell-main mono">{r.document}</span>
          {r.document === DOCUMENT_SETTINGS && isModel(r.image) && (
            <span className="cell-sub">what the model reads: provider, model, name</span>
          )}
          {r.document === DOCUMENT_REQUIRED && (
            <span className="cell-sub">
              {steward !== undefined ? `required by ${steward}` : "required by this workspace's admins"}
            </span>
          )}
        </span>
      ),
    },
    {
      key: "workload",
      header: "Workload",
      width: "minmax(0, 1.4fr)",
      value: (r) => r.name,
      cell: (r) => (
        <span className="cell-main">
          {workloadTo !== undefined ? <NameLink to={workloadTo(r)}>{r.name}</NameLink> : r.name}
        </span>
      ),
    },
    {
      key: "version",
      header: "Version",
      width: "110px",
      align: "end",
      value: (r) => r.revision,
      cell: (r) => <span className="muted">{r.revision}</span>,
    },
  ];
}
